import * as ko from "knockout";
import {IMessageData, IRouteDefinitionParams, ISystem} from '../../index';
import {nsRoute} from "../../util/route";
import {createErrorMessage, getKnockoutThrottle, isSupported} from "../../util/misc";
import {getHash, getRouteDropdownButton, setRouteHash} from "../../constants/RouteDefinitions";
import {global, lm} from "../../appContext";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {nsFa} from "../fa/fn";
import {REQUEST_THROTTLE, SYSTEM_MEGA_EADM} from "../../constants";
import pubsub from "../../pubsub";
import {TableBasic} from "../table/basic";
import {ITableColumnDef, ITableDef} from "../table/index";
import {storeObservable} from "../../util/sessionstore";
import {IParams as IEllipsisButtonParams} from "../button/ellipsis-dropdown"
import {ISearchObjectRouteOptions} from "@component/object";
import {withHashQuery} from "../../util/hashQuery";
import {ISplitButtonParams} from "../button/splitButton/split-button";
import {getSearchBoxHeadingKey} from "../search/boxHeadingKey";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import i = nsFa.i;
import searchHtml from './search.html?raw';

export const template = searchHtml;

export class viewModel extends ParsedRouteDataDecorator {

    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly searchkey: KnockoutObservable<string> = ko.observable('').extend(getKnockoutThrottle({rateLimit: REQUEST_THROTTLE}));
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly hasFocus: KnockoutObservable<boolean> = ko.observable(false);
    private readonly showAll: KnockoutObservable<boolean> = storeObservable('systemaccesssearch.collection.show.all')
    private readonly sortcol: KnockoutObservable<keyof ISystem> = ko.observable<keyof ISystem>('name');

    private tableDef!: TableBasic<ISystem>;
    private readonly hitCount: KnockoutObservable<number> = ko.observable(0);
    private readonly createButtonParams: ISplitButtonParams | null = global.permissionValidator.isMinCompanyAdmin()
        ? {mainButton: getRouteDropdownButton('createsystemaccess', {cls: 'btn-action btn-sm', icon: 'fa-plus'})}
        : null;

    /** White-box title key (`*.search`); distinct from route `label` used for tabs / page H1. */
    private readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    /** Heading badge: filtered hit count when searching / show-all; hidden when idle. */
    private readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady() || (!this.showAll() && !this.searchkey())) {
            return null;
        }
        return this.hitCount();
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    private readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    constructor(params: IRouteDefinitionParams) {
        super(params);

        pubsub.on(this, 'collection.needs.refresh', this.init.bind(this));

        this.init();
    }

    private init() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE})
        this.__requestRouteData!.request()
            .then((data: ISystem[]) => {
                const canManage = global.permissionValidator.isMinCompanyAdmin();

                const def: ITableDef<ISystem> = {
                    columns: [{
                        key: 'id',
                        searchable: true,
                        sortable: true,
                    }, {
                        getSortValue: o => o.name.toLowerCase(),
                        key: 'name',
                        label: 'collection',
                        searchable: true,
                        sortable: true,
                    }, {
                        contentType: 'html',
                        getContent: (o, c, v) => isSupported(v as boolean),
                        key: 'multiplerolesamedepartment',
                        label: '((multiple permissions)) ((per department|ac))',
                        searchable: true,
                        sortable: true,
                    }, {
                        contentType: 'html',
                        getContent: (o, c, v) => isSupported(v as boolean),
                        key: 'multiplerolesamecompany',
                        label: '((multiple permissions)) ((per organization|ac))',
                        searchable: true,
                        sortable: true,
                    }, {
                        getContent: o => o.roles?.length ?? 0,
                        getSortValue: o => o.roles?.length ?? 0,
                        key: 'roles',
                        label: 'number of subjects|s:((permission|ac|p))',
                        searchable: true,
                        sortable: true,
                    }, {
                        key: 'systemowner',
                        label: 'system owner',
                        getContent: o => o.systemowner || '—',
                        getTitle: o => o.systemowner,
                        searchable: true,
                        sortable: true,
                    }, {
                        key: 'func' as any,
                        label: '',
                        validate: () => canManage,
                        contentType: 'component',
                        getComponent: (system) => {
                            const params: IEllipsisButtonParams = {
                                buttons: [{
                                    label: lm.t('search for users with this permission'),
                                    href: () => {
                                        const options: ISearchObjectRouteOptions = {
                                            $resetFilters: true,
                                            showAll: true,
                                            systemid: system.id,
                                        };
                                        return withHashQuery(getHash('usersearch'), {
                                            resetFilters: options.$resetFilters,
                                            showAll: options.showAll,
                                            systemid: options.systemid,
                                        }) ?? ''
                                    },
                                }, {
                                    label: () => lm.t('Generate ruleset and message flow') ?? '',
                                    href: () => getHash('systemaccesswizardgenerate-rs-and-mf', {
                                        systemid: system.id,
                                    }) ?? '',
                                    validator: () => !system.definitionreadonly,
                                }, {
                                    label: lm.t('wizard for assigning permissions') ?? '',
                                    href: () => getHash('wizardpermissions', {
                                        systemid: system.id,
                                    }) ?? '',
                                    validator: () => !system.definitionreadonly,
                                }, {
                                    label: lm.t('create permissions from groups') ?? '',
                                    validator: () => !system.definitionreadonly,
                                    onClick: () => {
                                        global.dialogManager.addComponent({
                                            content: {
                                                name: 'component/systemaccess/dialog/groups-to-permissions',
                                                params: {
                                                    system: system,
                                                },
                                            },
                                            title: 'Create permissions from groups',
                                        })
                                    },
                                }, {
                                    label: lm.t('edit') ?? '',
                                    href: () => getHash('editsystemaccess', {id: system.id}) ?? '',
                                    validator: () => !system.isreadonly && !system.definitionreadonly,
                                }, {
                                    cls: 'btn-danger btn-ready-calm',
                                    label: lm.t('delete'),
                                    onClick: () => {
                                        global.dialogManager.addComponent({
                                            content: {
                                                name: 'component/systemaccess/dialog/purge-collection',
                                                params: {
                                                    system: system,
                                                }
                                            },
                                            title: `${i('fa-trash-alt')} ((delete subject|s:((collection|ac)))) <span class="name">[${system.name}]</span>`,
                                            onSuccess: () => this.init(),
                                            onError: () => this.init(),
                                        })
                                    },
                                    validator: () => !system.isreadonly && !system.definitionreadonly && system.name != SYSTEM_MEGA_EADM,
                                }]
                            }
                            return {
                                name: 'component/button/ellipsis-dropdown',
                                params: params
                            }
                        }

                    }],
                    data: data,
                    header: {
                        getContent: (col: ITableColumnDef<ISystem>) => lm.t(col.label ?? col.key) ?? '',
                        getTitle: (col: ITableColumnDef<ISystem>) => lm.t(col.label ?? col.key) ?? '',
                    },
                    navigation: {
                        pagenr: ko.observable(0),
                        totalresults: this.hitCount,
                    },
                    rows: {
                        onClick: (o: ISystem) => {
                            setRouteHash('systemaccessdetails', {id: o.id});
                        },
                    },
                    searchkey: ko.pureComputed((): string | null => {
                        if (this.showAll()) {
                            return ""
                        }
                        return this.searchkey() || null
                    }),
                    sort: {ascending: ko.observable(true), sortColumn: this.sortcol},
                    translationKey: 'collection',
                };

                this.tableDef = new TableBasic<ISystem>(def)

                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }

}
